import React, { useEffect, useRef, useState } from 'react';

import ChatUnreadDivider from './ChatUnreadDivider';
import { normalizeMsgId } from './timelineMerge';

/**
 * 「ここから未読」区切りの位置決め。Mobile `mobile/src/screens/chat/chatUnreadDivider.js` と同一契約。
 *
 * サーバーはメッセージ単位の「自分が読んだか」を返さない（個人チャットの `is_read` は
 * 相手が自分の発言を読んだかで意味が違う）。持っているのは会話単位の未読件数だけなので、
 * 「最新から N 件が未読」とみなして境界を出す。
 *
 * 開く位置は最新のまま変えない。未読が少ない時は区切り自体を出さない。
 */

/** これ未満の未読では区切りを出さない。 */
export const UNREAD_DIVIDER_MIN_COUNT = 10;

/**
 * 区切りを差し込むメッセージのidを返す。区切りはこのメッセージの**視覚的な直上**に描き、
 * 「ここから下（新しい側）が未読」を示す。
 *
 * @param {Array}  messages    DESC配列（index0=最新）
 * @param {number} unreadCount 会話の未読件数
 * @param {number} minCount    区切りを出す下限
 * @returns {string} 対象メッセージid。出さない場合は空文字。
 */
export const resolveUnreadDividerMessageId = (
  messages = [],
  unreadCount = 0,
  minCount = UNREAD_DIVIDER_MIN_COUNT
) => {
  const rows = Array.isArray(messages) ? messages : [];
  const count = Number(unreadCount) || 0;
  if (count < minCount || !rows.length) return '';
  // 未読が取得済みページより多い場合は、手元の最古を境界にする。
  const boundaryIndex = Math.min(count, rows.length) - 1;
  return normalizeMsgId(rows[boundaryIndex]?.id);
};

/**
 * 描画済みの行配列へ区切りを差し込む。
 *
 * Web の本文リストは `column-reverse` なので、DOM 上で境界メッセージの **後ろ** が
 * 視覚的な **上** になる。よって挿入位置は境界の index + 1。
 *
 * @param {Array} renderedRows `messages.map(...)` の結果（1メッセージ1要素）
 * @param {Array} messages     DESC配列
 * @param {string} dividerMessageId `resolveUnreadDividerMessageId` の戻り値
 */
export const withUnreadDivider = (renderedRows, messages = [], dividerMessageId = '') => {
  const rows = Array.isArray(renderedRows) ? renderedRows : [];
  const normalizedId = normalizeMsgId(dividerMessageId);
  if (!normalizedId) return rows;
  const index = (Array.isArray(messages) ? messages : []).findIndex(
    (message) => normalizeMsgId(message?.id) === normalizedId
  );
  if (index < 0 || index >= rows.length) return rows;
  const next = rows.slice();
  next.splice(index + 1, 0, <ChatUnreadDivider key="chat-unread-divider" />);
  return next;
};

export default resolveUnreadDividerMessageId;

/**
 * 会話を開いた時点の未読件数から区切り位置を決め、以後は動かさない。
 *
 * 開くと未読は 0 へ落ちるので、生の未読件数を見続けると区切りが即座に消える。
 * また受信のたびに再計算すると線が滑るため、会話ごとに一度だけ確定させる。
 *
 * @param {object} params
 * @param {string} params.conversationKey 会話の同一性。変わったら決め直す
 * @param {number} params.unreadCount     開いた時点で観測できる未読件数
 * @param {Array}  params.messages        DESC配列
 * @returns {string} 区切りを差し込むメッセージid（出さない場合は空文字）
 */
export const useChatUnreadDivider = ({ conversationKey = '', unreadCount = 0, messages = [] }) => {
  const [dividerMessageId, setDividerMessageId] = useState('');
  const resolvedRef = useRef(false);
  const openUnreadCountRef = useRef(0);

  useEffect(() => {
    resolvedRef.current = false;
    openUnreadCountRef.current = 0;
    setDividerMessageId('');
  }, [conversationKey]);

  // 開いた直後に未読が 0 へ落ちるまでの間に観測できた最大値を掴む。
  useEffect(() => {
    if (resolvedRef.current) return;
    const count = Number(unreadCount) || 0;
    if (count > openUnreadCountRef.current) openUnreadCountRef.current = count;
  }, [unreadCount]);

  useEffect(() => {
    if (resolvedRef.current) return;
    if (!Array.isArray(messages) || !messages.length) return;
    resolvedRef.current = true;
    setDividerMessageId(resolveUnreadDividerMessageId(messages, openUnreadCountRef.current));
  }, [messages]);

  return dividerMessageId;
};

export const useEventChatUnreadDivider = (eventId, unreadMap, messages) =>
  useChatUnreadDivider({
    conversationKey: `event:${eventId || ''}`,
    unreadCount: unreadMap instanceof Map ? Number(unreadMap.get(eventId) || 0) : 0,
    messages,
  });
